import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { siteApi } from '@/api/site'
import { resolveMediaUrl } from '@/utils/mediaUrl'

/**
 * The store's logo for the admin header and sidebar — the same one the storefront header shows: whatever was uploaded under Site
 * Settings → Logo, else the bundled Kinmel wordmark for the Kinmel store, else the store name as text. The wordmark has white
 * letters (it is designed for the storefront's coloured header), so it sits on a chip in the store's secondary colour.
 */
export default function BrandLogo({ compact = false, className = '' }: { compact?: boolean; className?: string }) {
  const { data } = useQuery({ queryKey: ['site', 'public'], queryFn: () => siteApi.getPublic(), staleTime: 5 * 60_000 })
  const site = data?.site
  const name = site?.applicationName || site?.headerTitle || 'Store'
  const uploaded = site?.logoUrl ? resolveMediaUrl(site.logoUrl) : null
  const src = uploaded || (/kinmel/i.test(name) ? '/brand/kinmel-logo.png' : null)
  const chip = site?.secondaryColor || '#1e3a8a'

  return (
    <Link to="/" aria-label={`${name} — dashboard`} className={`inline-flex items-center ${className}`}>
      {src && compact ? (
        // Collapsed sidebar (90 px wide): the full wordmark doesn't fit — show the store's initial on the brand-coloured chip.
        <span className="flex h-10 w-10 items-center justify-center rounded-lg text-lg font-bold text-white" style={{ backgroundColor: chip }} title={name}>
          {name.trim().charAt(0).toUpperCase()}
        </span>
      ) : src ? (
        <span className="inline-flex items-center rounded-lg px-2.5 py-1.5" style={{ backgroundColor: chip }}>
          <img src={src} alt={name} className="h-7 w-auto sm:h-8" />
        </span>
      ) : (
        <span className="text-lg font-bold text-gray-900 dark:text-white">{name}</span>
      )}
    </Link>
  )
}
